<template>
    <slot />
    <Teleport to="body">
        <pbl-dialog
            ref="dialogRef"
            :headline="currentImage?.fileName ?? ''"
            click-shield
            class="image-viewer-dialog"
        >
            <div class="image-viewer-modal">
                <div
                    v-if="currentImage"
                    class="image-viewer-modal__content"
                >
                    <div
                        v-if="isLoadingImage"
                        class="image-viewer-modal__content__loading"
                    >
                        <pbl-spinner />
                    </div>
                    <img
                        v-else-if="imageUrl"
                        :src="imageUrl"
                        :alt="currentImage.fileName ?? undefined"
                        class="image-viewer-modal__content__img"
                    />
                    <div
                        v-else
                        class="image-viewer-modal__content__placeholder"
                    >
                        <pbl-icon
                            name="image"
                            class="image-viewer-modal__content__placeholder__icon text-muted"
                        />
                        <p class="image-viewer-modal__content__placeholder__text">
                            {{ t('generators.images.modal.noImageAvailable') }}
                        </p>
                    </div>
                </div>
            </div>
            <pbl-button
                slot="footer"
                variant="secondary"
                @click="closeModal"
            >
                {{ t('common.close') }}
            </pbl-button>
        </pbl-dialog>
    </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { PebbleDialog } from '@pebble/core'
import { useImageBlobCacheStore } from '@/store/imageBlobCacheStore'
import { ImageDetailsDto } from '@/api/generated/HCA'

const { t } = useI18n()
const imageBlobCache = useImageBlobCacheStore()
const dialogRef = ref<PebbleDialog | null>(null)
const imageUrl = ref<string | null>(null)
const isLoadingImage = ref(false)
const currentImage = ref<ImageDetailsDto | null>(null)

const loadImage = async () => {
    if (!currentImage.value?.imagePath) {
        imageUrl.value = null
        return
    }

    const cached = imageBlobCache.getCachedImageUrl(currentImage.value.imagePath)
    if (cached) {
        imageUrl.value = cached
        return
    }

    isLoadingImage.value = true
    try {
        imageUrl.value = await imageBlobCache.getImageUrl(currentImage.value.imagePath)
    } catch (error) {
        console.error('Failed to load image:', error)
        imageUrl.value = null
    } finally {
        isLoadingImage.value = false
    }
}

const openModal = (image: ImageDetailsDto) => {
    currentImage.value = image
    dialogRef.value?.show()
    loadImage()
}

const closeModal = () => {
    dialogRef.value?.hide()
    imageUrl.value = null
    currentImage.value = null
}

defineExpose({
    openModal,
    closeModal,
})
</script>

<style scoped lang="scss">
.image-viewer-dialog {
    :deep(.pbl-dialog__content) {
        max-width: 90vw;
        max-height: 90vh;
    }
}

.image-viewer-modal {
    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-width: 500px;
        min-height: 400px;
        max-width: 80vw;
        max-height: 70vh;

        &__loading {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
        }

        &__img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
        }

        &__placeholder {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: $spacing-m;
            color: $pbl-text-muted;

            &__icon {
                --size: 7.5rem;
            }

            &__text {
                font-size: 1rem;
                font-weight: 500;
                margin: 0;
            }
        }
    }
}
</style>
